import { useMemo, useState } from 'react';
import { trierParNom } from '../../../server/lib/personnes';
import { aujourdhui } from '../../../server/lib/dates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import { ndfApi } from '../../services/ndf-api';
import {
  BoutonSecondaire,
  Champ,
  Saisie,
  Section,
  Selection,
} from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import {
  AjoutAttestation,
  FormulaireArret,
  FormulaireMiTemps,
  dateFr,
} from '../../components/conges/ArretsTravail';
import { DetailArret } from '../../components/conges/DetailArret';
import { useTypesArret } from '../../hooks/useTypesArret';

/**
 * Pilotage des arrêts de travail : tous les salariés, sur une seule vue.
 *
 * Le bloc de la fiche salarié répond à « où en est cette personne ». Celui-ci
 * répond à « où en est l'entreprise » : qui est arrêté en ce moment, quels
 * épisodes se prolongent, quelles attestations manquent. Deux questions
 * différentes, mais LE MÊME formulaire de saisie — en tenir deux versions
 * garantirait qu'une règle ajoutée à l'une manque à l'autre.
 *
 * Réservé à `gerer_absences_rh` : le manager n'atteint pas ces routes, et les
 * documents demandent en plus le droit « données médicales » (R-AT-04).
 */

// La date du jour vient de `server/lib/dates`, la SEULE arithmétique de dates du
// dépôt : elle la lit à PARIS. Découper `toISOString()` donnait le jour en temps
// universel, donc entre minuit et deux heures du matin un arrêt commencé le jour
// même n'apparaissait pas « en cours ».

export function CongesArretsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [saisie, setSaisie] = useState<null | { salarieId: string; arretInitialId?: string }>(
    null,
  );
  const [detail, setDetail] = useState<string | null>(null);
  const { types: typesArret } = useTypesArret();
  // Le mi-temps thérapeutique ne se saisissait QUE sur la fiche salarié, dans
  // un bloc replié d'un autre module : introuvable depuis l'écran où la RH
  // saisit justement les absences.
  const [mtt, setMtt] = useState<null | { salarieId: string }>(null);
  const [choixSalarie, setChoixSalarie] = useState('');
  const [filtreSalarie, setFiltreSalarie] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [filtreEtat, setFiltreEtat] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'arrets', 'tous'],
    queryFn: () => congesApi.absencesRh.tousLesArrets(),
  });
  const { data: fiches } = useQuery({
    queryKey: ['ndf', 'salaries'],
    queryFn: () => ndfApi.salaries.liste(),
    retry: false,
  });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['conges', 'arrets'] });
    qc.invalidateQueries({ queryKey: ['conges', 'demandes'] });
  };

  const ouvrirDocument = async (pieceId: string) => {
    try {
      const p = await congesApi.absencesRh.document(pieceId);
      const binaire = atob(p.contenu_base64);
      const octets = new Uint8Array(binaire.length);
      for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([octets], { type: p.mime ?? 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error(erreurGlobale(e) ?? 'Ce document ne vous est pas accessible.');
    }
  };

  const episodes = useMemo(() => {
    const jour = aujourdhui();
    return (data?.episodes ?? []).filter((e) => {
      if (filtreSalarie && e.salarie.id !== filtreSalarie) return false;
      if (filtreType && e.initial.type_arret !== filtreType) return false;
      // « En cours » se lit sur l'épisode entier, prolongations comprises : un
      // arrêt initial terminé mais prolongé est toujours en cours.
      const enCours = e.debut <= jour && e.fin >= jour;
      if (filtreEtat === 'en_cours' && !enCours) return false;
      if (filtreEtat === 'termine' && enCours) return false;
      // La période filtre sur le CHEVAUCHEMENT : un arrêt de six semaines
      // concerne chacun des mois qu'il traverse.
      if (fin && e.debut > fin) return false;
      if (debut && e.fin < debut) return false;
      return true;
    });
  }, [data, filtreSalarie, filtreType, filtreEtat, debut, fin]);

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;

  // Un mandataire social n'est concerné ni par les congés, ni par les arrêts de
  // travail : il ne doit pas figurer dans ces sélecteurs. Le serveur refuse de
  // toute façon la saisie, mais proposer un nom qu'on ne peut pas retenir fait
  // perdre du temps et laisse croire à une panne.
  const salaries = (fiches?.salaries ?? []).filter((s) => s.beneficie_conges !== false);
  const jour = aujourdhui();
  const enCours = (data?.episodes ?? []).filter((e) => e.debut <= jour && e.fin >= jour);

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">Arrêts de travail</h1>
          <p className="text-sm text-nuit/60">
            {enCours.length === 0
              ? 'Personne n’est en arrêt aujourd’hui.'
              : `${enCours.length} arrêt(s) en cours aujourd’hui.`}
          </p>
        </div>
        {!saisie && !mtt && (
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire
              type="button"
              onClick={() => setSaisie({ salarieId: choixSalarie || salaries[0]?.id || '' })}
            >
              Déclarer un arrêt
            </BoutonSecondaire>
            <BoutonSecondaire
              type="button"
              onClick={() => setMtt({ salarieId: choixSalarie || salaries[0]?.id || '' })}
            >
              Mi-temps thérapeutique
            </BoutonSecondaire>
          </div>
        )}
      </header>

      {saisie && (
        <Section titre="Nouvel arrêt" teinte="corail">
          <Champ label="Salarié" requis>
            <Selection
              value={saisie.salarieId}
              onChange={(e) => {
                setChoixSalarie(e.target.value);
                setSaisie({ salarieId: e.target.value });
              }}
            >
              <option value="">Choisir</option>
              {salaries.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.prenom} {s.nom}
                </option>
              ))}
            </Selection>
          </Champ>
          {saisie.salarieId && (
            <FormulaireArret
              salarieId={saisie.salarieId}
              arretInitialId={saisie.arretInitialId ?? null}
              episodes={[]}
              onTermine={() => {
                setSaisie(null);
                rafraichir();
              }}
              onAnnuler={() => setSaisie(null)}
            />
          )}
        </Section>
      )}

      {mtt && (
        <Section titre="Mi-temps thérapeutique" teinte="violet">
          <Champ label="Salarié" requis>
            <Selection
              value={mtt.salarieId}
              onChange={(e) => {
                setChoixSalarie(e.target.value);
                setMtt({ salarieId: e.target.value });
              }}
            >
              <option value="">Choisir</option>
              {salaries.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.prenom} {s.nom}
                </option>
              ))}
            </Selection>
          </Champ>
          {mtt.salarieId && (
            <FormulaireMiTemps
              salarieId={mtt.salarieId}
              episodes={(data?.episodes ?? []).filter((e) => e.salarie.id === mtt.salarieId)}
              onTermine={() => {
                setMtt(null);
                rafraichir();
              }}
              onAnnuler={() => setMtt(null)}
            />
          )}
        </Section>
      )}

      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-3">
        <Champ label="Salarié">
          <Selection value={filtreSalarie} onChange={(e) => setFiltreSalarie(e.target.value)}>
            <option value="">Tout le monde</option>
            {trierParNom([
              ...new Map((data?.episodes ?? []).map((e) => [e.salarie.id, e.salarie])).values(),
            ]).map(
              (s) => (
                <option key={s.id} value={s.id}>
                  {s.prenom} {s.nom}
                </option>
              ),
            )}
          </Selection>
        </Champ>
        <Champ label="Type d’arrêt">
          <Selection value={filtreType} onChange={(e) => setFiltreType(e.target.value)}>
            <option value="">Tous les types</option>
            {typesArret.map((t) => (
              <option key={t.id} value={t.libelle}>
                {t.libelle}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="État">
          <Selection value={filtreEtat} onChange={(e) => setFiltreEtat(e.target.value)}>
            <option value="">Tous</option>
            <option value="en_cours">En cours</option>
            <option value="termine">Terminés</option>
          </Selection>
        </Champ>
        <Champ label="À partir du">
          <Saisie type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </Champ>
        <Champ label="Jusqu’au">
          <Saisie type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
        </Champ>
      </div>

      <p className="text-sm text-nuit/60">{episodes.length} épisode(s).</p>

      {episodes.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-nuit/60">
          Aucun arrêt ne correspond à ces filtres.
        </p>
      ) : (
        <ul className="space-y-2">
          {episodes.map((e) => {
            const actif = e.debut <= jour && e.fin >= jour;
            return (
              <li key={e.initial.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  {/* R-AT-06 : la ligne OUVRE le détail, comme une case de
                      calendrier. Sans cela, prolonger un arrêt obligeait à
                      passer par la fiche du salarié, dans un autre module. */}
                  <button
                    type="button"
                    onClick={() => setDetail(e.initial.id)}
                    className="flex-1 text-left"
                  >
                    <p className="font-medium text-nuit">
                      {e.salarie.prenom} {e.salarie.nom}{' '}
                      <span className="font-normal text-nuit/60">({e.initial.type_arret})</span>
                    </p>
                    <p className="text-sm text-nuit/70">
                      Du {dateFr(e.debut)} au {dateFr(e.fin)}
                      {e.prolongations.length > 0 &&
                        ` (${e.prolongations.length} prolongation${e.prolongations.length > 1 ? 's' : ''})`}
                    </p>
                  </button>
                  <span
                    className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                      actif ? 'bg-corail-fond/40 text-corail' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {actif ? 'En cours' : 'Terminé'}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {e.attestations.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => void ouvrirDocument(a.id)}
                      className="rounded bg-slate-100 px-2 py-1 text-xs text-nuit/80 hover:bg-slate-200"
                    >
                      {a.filename}
                    </button>
                  ))}
                  <AjoutAttestation arretId={e.initial.id} onFait={rafraichir} />
                </div>

                {detail === e.initial.id && (
                  <div className="mt-3">
                    <DetailArret
                      arretId={e.initial.id}
                      onFerme={() => setDetail(null)}
                      actions={
                        <BoutonSecondaire
                          type="button"
                          onClick={() =>
                            setSaisie({ salarieId: e.salarie.id, arretInitialId: e.initial.id })
                          }
                        >
                          Prolonger
                        </BoutonSecondaire>
                      }
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
